<template>
  <div>
    <ui-dialog
      :show="isOpen"
      max-width="867"
      @input="handleClose"
      class="reply-templates-categories"
    >
      <template #header>
        <div class="reply-templates-categories__header">
          <h3>
            {{ $t("message.reviews.replyTemplates.title") }}
          </h3>
        </div>
      </template>

      <div class="reply-templates-categories__body">
        <div
          v-if="categories && categories.length === 0"
          class="reply-templates-categories__empty"
        >
          <p>
            {{
              $t(
                "message.reviews.replyTemplates.categories.noCategoriesDescription"
              )
            }}
          </p>
          <ui-button
            @click="initAddCategory"
            :disabled="loadingList"
            color="primary"
            outlined
            icon="icon-plus"
            >{{
              $t(
                `message.reviews.replyTemplates.categories.${DIALOG_MANAGE_MODE.ADD}`
              )
            }}</ui-button
          >
        </div>
        <div v-else-if="categories">
          <div class="d-flex align-center justify-space-between mb-5">
            <ui-button
              @click="initAddCategory"
              :disabled="loadingList"
              color="primary"
              outlined
              icon="icon-plus"
            >
              {{
                $t(
                  `message.reviews.replyTemplates.categories.${DIALOG_MANAGE_MODE.ADD}`
                )
              }}
            </ui-button>

            <!-- <div>TODO: pagination</div> -->
          </div>

          <div class="table-header">
            <div class="table-header__category-name">
              {{ $t("message.reviews.replyTemplates.categories.category") }}
            </div>
            <div class="table-header__replies-count">
              {{ $t("message.reviews.replyTemplates.categories.repliesCount") }}
            </div>
            <!-- <div class="table-header__date">
              {{ $t("message.reviews.replyTemplates.categories.createdBy") }}
            </div>
            <div class="table-header__actions"></div> -->
          </div>
          <v-list class="reply-templates-categories__list">
            <v-virtual-scroll
              :items="categories"
              :item-height="categoryListItemHeight"
              :height="categoriesListHeight"
              class="mb-4"
            >
              <template v-slot:default="{ item }">
                <ReplyTemplatesCategoryListItem
                  :key="item.id"
                  :category="item"
                  @edit="initEditCategory"
                  @delete="deleteCategory"
                  @goToReplyTemplates="goToReplyTemplates"
                />
              </template>
            </v-virtual-scroll>
          </v-list>
        </div>
        <SectionLoader :status="loadingList" :opacity="0.75" />
      </div>
    </ui-dialog>
    <ReplyTemplatesManageCategoryDialog
      :is-open="dialog.manageCategory"
      :category-to-edit="categoryToEdit"
      :mode="manageCategoryMode"
      @save="saveCategory"
      @edit="editCategory"
      @close="closeManageCategoryDialog"
    />
    <ReplyTemplatesDialog
      :category="chosenReplyTemplatesCategory"
      :is-open="dialog.replyTemplates"
      @close="closeTemplatesDialog"
      @save="saveCategory"
    />
  </div>
</template>

<script>
import SectionLoader from "@/components/Loader/SectionLoader";
import ReplyTemplatesManageCategoryDialog from "@/components/Reviews/ReplyTemplates/ManageCategoryDialog";
import ReplyTemplatesCategoryListItem from "@/components/Reviews/ReplyTemplates/CategoryListItem";
import ReplyTemplatesDialog from "@/components/Reviews/ReplyTemplates/ReplyTemplatesDialog";

import { DIALOG_MANAGE_MODE } from "@/enums/reply-templates";
import { gtmEvent__reviewTemplateManageAction } from "@/helpers/gtm-events";

import serviceHandler from "@/helpers/handlers/service";
import reviewsService from "@/services/reviews";

export default {
  name: "ReplyTemplatesCategoriesDialog",
  components: {
    SectionLoader,
    ReplyTemplatesManageCategoryDialog,
    ReplyTemplatesCategoryListItem,
    ReplyTemplatesDialog
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      DIALOG_MANAGE_MODE,
      dialog: {
        manageCategory: false,
        replyTemplates: false
      },
      manageCategoryMode: null,
      categoryToEdit: null,
      loadingList: false,
      categories: null,
      categoryListItemHeight: 64,
      chosenReplyTemplatesCategory: null
    };
  },

  computed: {
    categoriesListHeight() {
      if (!this.categories) return 300;

      const minVisibleListItems = 4;
      const maxVisibleListItems = 8;
      const categoriesCount = this.categories.length;

      return categoriesCount > maxVisibleListItems
        ? this.categoryListItemHeight * maxVisibleListItems
        : this.categoryListItemHeight *
            Math.max(categoriesCount, minVisibleListItems);
    }
  },

  watch: {
    async isOpen(newValue) {
      if (newValue) {
        await this.fetchReplyCategories();
      } else {
        this.categories = null;
      }
    }
  },

  methods: {
    async fetchReplyCategories() {
      this.loadingList = true;
      this.categories = await serviceHandler(
        reviewsService.getReplyCategories()
      );
      this.loadingList = false;
    },

    async saveCategory(categoryName) {
      const payload = { name: categoryName };

      const response = await serviceHandler(
        reviewsService.postReplyCategory(payload)
      );

      if (response) {
        await this.fetchReplyCategories();
        gtmEvent__reviewTemplateManageAction({ action: "category_add" });
      }
      this.manageCategoryMode = null;
    },

    initAddCategory() {
      this.categoryToEdit = null;
      this.manageCategoryMode = DIALOG_MANAGE_MODE.ADD;
      this.dialog.manageCategory = true;
    },

    initEditCategory(category) {
      this.categoryToEdit = category;
      this.manageCategoryMode = DIALOG_MANAGE_MODE.EDIT;
      this.dialog.manageCategory = true;
    },

    async editCategory(category) {
      this.loadingList = true;

      const response = await serviceHandler(
        reviewsService.patchReplyCategory(category)
      );

      if (response) {
        await this.fetchReplyCategories();
        gtmEvent__reviewTemplateManageAction({ action: "category_edit" });
      }

      this.categoryToEdit = null;
      this.manageCategoryMode = null;
      this.loadingList = false;
    },

    async deleteCategory(category) {
      this.loadingList = true;

      await serviceHandler(reviewsService.deleteReplyCategory(category));
      await this.fetchReplyCategories();
      gtmEvent__reviewTemplateManageAction({ action: "category_remove" });

      this.loadingList = false;
    },

    goToReplyTemplates(category) {
      this.chosenReplyTemplatesCategory = category;
      this.dialog.replyTemplates = true;
    },

    async closeTemplatesDialog() {
      this.dialog.replyTemplates = false;
      await this.fetchReplyCategories();
      this.chosenReplyTemplatesCategory = null;
    },

    closeManageCategoryDialog() {
      this.dialog.manageCategory = false;
      this.manageCategoryMode = null;
    },

    handleClose() {
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss" scoped>
.reply-templates-categories {
  &__header {
    flex: 1 1 auto;
    padding-bottom: $spacing * 2.5;
    border-bottom: 1px solid $light-3;
  }

  &__body {
    min-height: 200px;
    position: relative;
  }

  &__empty {
    min-height: 200px;
    padding-top: $spacing * 2.5;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    p {
      color: $text;
      text-align: center;
      max-width: 70%;
      white-space: pre-line;
      margin-bottom: $spacing * 3.5;
    }
  }

  &__list {
    list-style: none;
    padding-left: 0;
  }
}

.table-header {
  display: flex;
  padding-left: $spacing * 2;
  margin-bottom: $spacing;
  @include font-size(12px);

  &__category-name,
  &__actions {
    width: 30%;
  }

  &__replies-count,
  &__date {
    width: 20%;
  }
}
</style>
